import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Search, 
  X,
  Loader,
  Edit2,
  Plus,
  Package,
  AlertCircle,
  CheckCircle,
  Trash2
} from 'lucide-react';
import { Can } from '../components/Auth/Can';
import TipoCajaModal from '../components/Products/TipoCajaModal';
import DeleteTipoCajaModal from '../components/Products/DeleteTipoCajaModal';
import {
  selectFilteredTiposCaja,
  selectTiposCajaFilters,
  selectTiposCajaLoading,
  selectTiposCajaError,
  setFilters,
  clearFilters,
  fetchTiposCaja,
  createTipoCaja,
  updateTipoCaja,
  deleteTipoCaja,
  clearError
} from '../store/slices/tipoCajaSlice';
import './TipoCaja.css';

const TipoCaja = () => {
  const dispatch = useDispatch();
  const tiposCaja = useSelector(selectFilteredTiposCaja);
  const filters = useSelector(selectTiposCajaFilters);
  const loading = useSelector(selectTiposCajaLoading);
  const error = useSelector(selectTiposCajaError);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTipoCaja, setEditingTipoCaja] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingTipoCaja, setDeletingTipoCaja] = useState(null);

  // Cargar tipos de caja al montar el componente
  useEffect(() => {
    dispatch(fetchTiposCaja());
  }, [dispatch]);

  // Limpiar mensajes de éxito después de 3 segundos
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Limpiar errores después de 5 segundos
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleOpenModal = (tipoCaja = null) => {
    setEditingTipoCaja(tipoCaja);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTipoCaja(null);
  };

  const handleSaveTipoCaja = async (tipoCajaData) => {
    try {
      if (editingTipoCaja) {
        await dispatch(updateTipoCaja({ 
          id: editingTipoCaja.id, 
          tipoCajaData 
        })).unwrap();
        setSuccessMessage('Tipo de caja actualizado exitosamente');
      } else {
        await dispatch(createTipoCaja(tipoCajaData)).unwrap();
        setSuccessMessage('Tipo de caja creado exitosamente');
      }
      handleCloseModal();
    } catch (err) {
      // Error ya manejado por el slice
      console.error('Error al guardar tipo de caja:', err);
    }
  };

  const handleOpenDeleteModal = (tipoCaja) => {
    setDeletingTipoCaja(tipoCaja);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingTipoCaja(null);
  };

  const handleDeleteTipoCaja = async (id) => {
    try {
      await dispatch(deleteTipoCaja(id)).unwrap();
      setSuccessMessage('Tipo de caja eliminado permanentemente');
      handleCloseDeleteModal();
    } catch (err) {
      console.error('Error al eliminar tipo de caja:', err);
      // Re-throw the error so the modal can display it
      throw err;
    }
  };

  return (
    <div className="tipo-caja-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Tipos de Caja
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            Gestiona los tipos de caja disponibles para productos
          </p>
        </div>
        <Can I="create" a="products:boxes">
          <button 
            className="btn btn-primary"
            onClick={() => handleOpenModal()}
          >
            <Plus size={20} />
            Nuevo Tipo de Caja
          </button>
        </Can>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card">
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por tipo o descripción..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-select-group">
            <label className="filter-label">Estado</label>
            <select
              className="filter-select"
              value={filters.activo === undefined ? 'all' : filters.activo ? 'true' : 'false'}
              onChange={(e) => handleFilterChange('activo', e.target.value === 'all' ? undefined : e.target.value === 'true')}
            >
              <option value="all">Todos</option>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </div>
          
          {(filters.search || filters.activo !== undefined) && (
            <button 
              className="btn btn-outline"
              onClick={handleClearFilters}
            >
              <X size={20} />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card table-card">
        {loading ? (
          <div className="loading-container">
            <Loader className="spinner" size={40} />
            <p>Cargando tipos de caja...</p>
          </div>
        ) : tiposCaja.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <h3>No hay tipos de caja</h3>
            <p>
              {filters.search || filters.activo === false
                ? 'No se encontraron tipos de caja con los filtros aplicados'
                : 'Comienza creando tu primer tipo de caja'}
            </p>
            {!filters.search && filters.activo !== false && (
              <Can I="create" a="products:boxes">
                <button 
                  className="btn btn-primary"
                  onClick={() => handleOpenModal()}
                >
                  <Plus size={20} />
                  Crear Tipo de Caja
                </button>
              </Can>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th>Fecha Creación</th>
                  <th>Última Modificación</th>
                  <th className="actions-column">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tiposCaja.map((tipo) => (
                  <tr key={tipo.id}>
                    <td>
                      <div className="tipo-badge">
                        <Package size={16} />
                        <strong>{tipo.tipo}</strong>
                      </div>
                    </td>
                    <td>{tipo.descripcion}</td>
                    <td>
                      <span className={`status-badge ${tipo.activo ? 'status-active' : 'status-inactive'}`}>
                        {tipo.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      {tipo.fecha_creacion 
                        ? new Date(tipo.fecha_creacion).toLocaleDateString('es-CL')
                        : '-'}
                    </td>
                    <td>
                      {tipo.fecha_modificacion 
                        ? new Date(tipo.fecha_modificacion).toLocaleDateString('es-CL')
                        : '-'}
                    </td>
                    <td className="actions-column">
                      <div className="action-buttons">
                        <Can I="update" a="products:boxes">
                          <button
                            className="btn-icon btn-icon-edit"
                            onClick={() => handleOpenModal(tipo)}
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                        </Can>
                        <Can I="delete" a="products:boxes">
                          <button
                            className="btn-icon btn-icon-delete"
                            onClick={() => handleOpenDeleteModal(tipo)}
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      <TipoCajaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTipoCaja}
        tipoCaja={editingTipoCaja}
      />

      {/* Delete Modal */}
      {isDeleteModalOpen && deletingTipoCaja && (
        <DeleteTipoCajaModal
          tipoCaja={deletingTipoCaja}
          onClose={handleCloseDeleteModal}
          onDelete={handleDeleteTipoCaja}
        />
      )}
    </div>
  );
};

export default TipoCaja;
